/**
 * Modal Component
 * Overlay modal displaying vendor details with stat bars
 * @component
 */

import React, { useEffect } from 'react';
import { ModalProps } from '../types';
import { StatBar } from './StatBar';
import { Button } from './Button';
import { metricConfigs } from '../data/vendors';

export const Modal: React.FC<ModalProps> = ({ vendor, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!vendor) return null;

  return (
    <div
      className={`modal fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm transition-all duration-250 ${
        isOpen ? 'visible opacity-100' : 'invisible opacity-0'
      }`}
      aria-modal="true"
      role="dialog"
      aria-labelledby="modal-title"
    >
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-label="Close modal"
      />
      <div
        className={`modal-content relative bg-gray-800 rounded-2xl shadow-2xl w-11/12 max-w-3xl max-h-[90vh] overflow-y-auto transform transition-transform duration-250 ${
          isOpen ? 'scale-100' : 'scale-95'
        }`}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
          aria-label="Close modal"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-8 md:p-12">
          <div className="flex flex-col md:flex-row items-start gap-8">
            <div className="flex-shrink-0 text-center">
              <div className="bg-green-600 text-white text-3xl font-bold w-24 h-24 rounded-full flex items-center justify-center border-4 border-gray-700 shadow-lg">
                #{vendor.rank}
              </div>
              <div className="mt-2 text-xl font-bold text-green-400">
                {vendor.score.toFixed(2)}
              </div>
              <div className="text-sm text-gray-400">Overall Score</div>
            </div>

            <div className="w-full">
              <h2 id="modal-title" className="text-3xl font-extrabold text-white">
                {vendor.name}
              </h2>
              <p className="mt-2 text-gray-300">{vendor.summary}</p>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-700 pt-8">
            <h4 className="text-lg font-bold text-white mb-4">Ranking Breakdown</h4>
            {metricConfigs.map((config) => (
              <StatBar
                key={config.key}
                label={config.label}
                score={vendor[config.key]}
                colorClass={config.colorClass}
              />
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button href={vendor.website} variant="primary" external>
              Visit Website
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
